import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, Camera, ClipboardText, IdentificationCard, Key, MapPin, Money, PencilSimple, Plus, SignOut, Timer, Trophy, UserCircle, Users, WhatsappLogo } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, fileUrl, trackPage, formatApiErrorDetail } from "../lib/api";
import { subscribeContentUpdates } from "../lib/contentSync";
import BrandLogo from "../components/BrandLogo";
import TimeProgressChart from "../components/TimeProgressChart";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import PaymentDialog, { formatMonthLabel, formatRupiahValue } from "../components/PaymentDialog";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

function groupTimeRecordsByRace(records) {
    const map = {};
    records.forEach((record) => {
        const key = (record.race_number || "").trim() || "Tanpa Nomor Lomba";
        (map[key] = map[key] || []).push(record);
    });
    return Object.keys(map).sort().map((name) => {
        const items = map[name].slice().sort((a, b) => (b.record_date || "").localeCompare(a.record_date || ""));
        const best = items.reduce((current, record) => (!current || (record.time_ms || Infinity) < (current.time_ms || Infinity) ? record : current), null);
        return { name, items, best };
    });
}

function DuesPaymentSection({ child, index, payments, billing, submissions, onPay }) {
    const billingChild = (billing?.children || []).find((c) => c.child_id === child.id);
    const childPayments = payments.filter((payment) => payment.athlete_id === child.id);
    const childSubmissions = submissions.filter((s) => s.child_id === child.id);
    const hasUnpaid = (billingChild?.months || []).some((m) => m.status === "unpaid");
    const hasPendingSubmission = childSubmissions.some((s) => s.status === "pending");

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-racing-red"><Money size={18} /> Iuran & Pembayaran</h3>
                {billingChild && hasUnpaid && !billingChild.registration_required && !hasPendingSubmission && (
                    <button
                        type="button"
                        onClick={() => onPay(billingChild, "dues")}
                        data-testid={`parent-pay-dues-${index}`}
                        className="inline-flex items-center gap-2 bg-racing-red hover:bg-racing-redhover text-white text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 transition-colors duration-300"
                    >
                        <Money size={14} weight="bold" /> Bayar Iuran
                    </button>
                )}
            </div>

            {billingChild?.registration_required && (
                <div className="mb-4 border-2 border-racing-gold/60 bg-racing-gold/5 p-5" data-testid={`parent-registration-payment-${index}`}>
                    <p className="font-display uppercase text-lg text-neutral-900">Biaya Pendaftaran Siswa Baru</p>
                    <p className="text-sm text-racing-muted mt-1">
                        Pendaftaran {formatRupiahValue(billingChild.registration_fee)} + iuran bulan pertama ({formatMonthLabel(billingChild.first_month)}) {formatRupiahValue(billingChild.monthly_amount)}
                    </p>
                    <p className="font-display text-3xl text-racing-red mt-2">{formatRupiahValue(billingChild.first_payment_total)}</p>
                    {billingChild.registration_pending ? (
                        <p className="mt-3 text-xs uppercase tracking-[0.15em] text-racing-golddeep" data-testid={`parent-registration-pending-${index}`}>Pengajuan pembayaran sedang menunggu konfirmasi admin</p>
                    ) : (
                        <button
                            type="button"
                            onClick={() => onPay(billingChild, "registration")}
                            data-testid={`parent-pay-registration-${index}`}
                            className="mt-4 inline-flex items-center gap-2 bg-racing-red hover:bg-racing-redhover text-white text-[10px] uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300"
                        >
                            <Money size={14} weight="bold" /> Bayar Pendaftaran Sekarang
                        </button>
                    )}
                </div>
            )}

            {billingChild && billingChild.months.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid={`parent-dues-months-${index}`}>
                    {billingChild.months.map((m) => (
                        <span
                            key={m.month}
                            data-testid={`parent-dues-month-${index}-${m.month}`}
                            className={`text-[10px] uppercase tracking-[0.15em] px-3 py-2 border ${
                                m.status === "paid"
                                    ? "border-green-600/50 bg-green-50 text-green-700"
                                    : m.status === "pending"
                                        ? "border-racing-gold bg-racing-gold/10 text-racing-golddeep"
                                        : "border-racing-line bg-white text-racing-muted"
                            }`}
                        >
                            {formatMonthLabel(m.month)} • {m.status === "paid" ? "Lunas" : m.status === "pending" ? "Menunggu" : "Belum Bayar"}
                        </span>
                    ))}
                </div>
            )}

            {hasPendingSubmission && (
                <p className="mb-4 text-xs uppercase tracking-[0.15em] text-racing-golddeep" data-testid={`parent-payment-waiting-${index}`}>
                    Ada pengajuan pembayaran yang sedang diperiksa admin — iuran bisa dibayar lagi setelah selesai
                </p>
            )}

            {childPayments.length === 0 ? (
                <div className="bg-racing-surface border border-racing-line p-4 text-sm text-racing-muted">Belum ada iuran yang tercatat.</div>
            ) : (
                <div className="grid md:grid-cols-2 gap-3">
                    {childPayments.map((payment, paymentIndex) => (
                        <div key={payment.id} className="bg-racing-surface border border-racing-line p-4" data-testid={`parent-payment-${index}-${paymentIndex}`}>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-golddeep">{payment.period_month}</p>
                            <p className="mt-2 font-display text-3xl text-racing-red">{formatRupiah(payment.amount)}</p>
                            <p className="mt-2 text-xs text-racing-muted">{payment.payment_date} • {payment.method} • {payment.receipt_no}</p>
                            {payment.receipt_url && <a href={fileUrl(payment.receipt_url)} target="_blank" rel="noopener noreferrer" data-testid={`parent-receipt-${index}-${paymentIndex}`} className="mt-3 inline-block text-xs uppercase tracking-[0.2em] text-racing-red">Lihat Kwitansi</a>}
                        </div>
                    ))}
                </div>
            )}

            {childSubmissions.length > 0 && (
                <div className="mt-4 space-y-2" data-testid={`parent-submissions-${index}`}>
                    {childSubmissions.map((submission, subIndex) => (
                        <div key={submission.id} className="flex flex-wrap items-center justify-between gap-2 border border-racing-line bg-white px-4 py-3 text-sm" data-testid={`parent-submission-${index}-${subIndex}`}>
                            <span className="text-neutral-900">
                                {submission.type === "registration" ? "Pendaftaran + iuran pertama" : `Iuran ${(submission.months || []).map(formatMonthLabel).join(", ")}`}
                            </span>
                            <span className="flex items-center gap-3">
                                <span className="font-display text-racing-red">{formatRupiahValue(submission.amount)}</span>
                                <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${
                                    submission.status === "approved"
                                        ? "border-green-600/50 bg-green-50 text-green-700"
                                        : submission.status === "rejected"
                                            ? "border-racing-red/50 bg-red-50 text-racing-red"
                                            : "border-racing-gold bg-racing-gold/10 text-racing-golddeep"
                                }`}>
                                    {submission.status === "approved" ? "Disetujui" : submission.status === "rejected" ? `Ditolak${submission.reject_reason ? `: ${submission.reject_reason}` : ""}` : "Menunggu Konfirmasi"}
                                </span>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div className="bg-racing-surface border border-racing-line p-4">
            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-1.5">{label}</p>
            <p className="text-sm text-neutral-900 whitespace-pre-line">{value || "—"}</p>
        </div>
    );
}

function formatReportValue(value) {
    if (Array.isArray(value)) return value.join(", ");
    if (value === 0) return "0";
    return value || "—";
}

const REPORT_CLOSING_MESSAGE = "Berikut adalah progress latihan atlet saat ini. Senang bisa menemani setiap proses anak Bapak/Ibu. Bukan hasil akhir jadi juara yang kita kejar, tetapi setiap proses yang dilalui dan perjuangan anak Bapak/Ibu menjadi hal yang patut untuk terus diapresiasi";

function formatReportPeriod(period) {
    return /^\d{4}-\d{2}$/.test(period || "") ? formatMonthLabel(period) : period || "—";
}

const MONTH_SHORT_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

function shortPeriodLabel(period) {
    const [year, month] = (period || "").split("-");
    return `${MONTH_SHORT_ID[Number(month) - 1] || month} ${String(year || "").slice(2)}`;
}

function buildReportProgress(reports, athleteId) {
    const series = {};
    reports
        .filter((r) => r.athlete_id === athleteId && /^\d{4}-\d{2}$/.test(r.period || ""))
        .forEach((r) => {
            (r.template?.fields || []).forEach((field) => {
                if (!["number", "scale", "percent"].includes(field.type)) return;
                const value = r.answers?.[field.id];
                if (value == null || value === "") return;
                const key = `${r.template?.title || "Rapot"} — ${field.label}`;
                if (!series[key]) series[key] = { key, type: field.type, points: [] };
                series[key].points.push({ period: r.period, value: Number(value) });
            });
        });
    return Object.values(series)
        .map((s) => ({ ...s, points: s.points.sort((a, b) => a.period.localeCompare(b.period)) }))
        .filter((s) => s.points.length >= 2);
}

function ReportProgressChart({ series }) {
    const data = series.points.map((p) => ({ name: shortPeriodLabel(p.period), value: p.value }));
    const maxValue = series.type === "percent" ? 100 : series.type === "scale" ? 5 : undefined;
    return (
        <div>
            <p className="text-xs text-neutral-900 mb-1">
                {series.key}
                <span className="text-racing-muted">{series.type === "percent" ? " (%)" : series.type === "scale" ? " (/5)" : ""}</span>
            </p>
            <ResponsiveContainer width="100%" height={140}>
                <LineChart data={data} margin={{ top: 8, right: 12, bottom: 2, left: 0 }}>
                    <CartesianGrid stroke="#EAE3D8" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6E675C" }} tickLine={false} axisLine={{ stroke: "#EAE3D8" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#6E675C" }} tickLine={false} axisLine={false} width={36} domain={maxValue ? [0, maxValue] : ["auto", "auto"]} />
                    <Tooltip formatter={(value) => [series.type === "percent" ? `${value}%` : series.type === "scale" ? `${value}/5` : value, "Nilai"]} contentStyle={{ fontSize: 12, border: "1px solid #EAE3D8", background: "#fff" }} />
                    <Line type="monotone" dataKey="value" stroke="#E02424" strokeWidth={2.5} dot={{ r: 3.5, fill: "#E02424" }} activeDot={{ r: 5 }} />
                </LineChart>
            </ResponsiveContainer>
            <p className="text-[10px] text-racing-muted">Nilai terbaru: <strong className="text-racing-red">{series.type === "percent" ? `${data[data.length - 1].value}%` : series.type === "scale" ? `${data[data.length - 1].value}/5` : data[data.length - 1].value}</strong> ({data[data.length - 1].name})</p>
        </div>
    );
}

function formatRupiah(value) {
    return `Rp ${Number(value || 0).toLocaleString("id-ID")}`;
}

function toInputDate(value) {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return "";
    const [, day, month, year] = match;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

const CLASS_OPTIONS = [
    "Tim Hijau (Pemula)",
    "Tim Kuning",
    "Tim Jingga",
    "Tim Biru",
    "Tim Pink",
    "Tim Merah",
    "Speed",
    "Slalom",
];

const EMPTY_NEW_CHILD = {
    full_name: "",
    nickname: "",
    birth_place: "",
    birth_date: "",
    gender: "",
    joined_year: new Date().getFullYear(),
    class_name: "",
    religion: "",
    address: "",
    referral_source: "",
    promo_code: "",
    photo_url: "",
};

export default function ParentDashboard() {
    const { user, logout, updateUser } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [billing, setBilling] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [paymentTarget, setPaymentTarget] = useState(null);
    const [reports, setReports] = useState([]);
    const visibleReports = reports.filter((report) => report.template_title || Object.keys(report.answers || {}).length > 0);
    const [payments, setPayments] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [timeRecords, setTimeRecords] = useState([]);
    const [achievements, setAchievements] = useState([]);
    const [achievementForm, setAchievementForm] = useState({ athlete_id: "", title: "", event: "", year: new Date().getFullYear(), medal: "gold", category: "Speed", level: "Kota/Kabupaten", image_url: "" });
    const [eventList, setEventList] = useState([]);
    const [achievementUploading, setAchievementUploading] = useState(false);
    const [achievementSaving, setAchievementSaving] = useState(false);
    const [editChild, setEditChild] = useState(null);
    const [childForm, setChildForm] = useState(null);
    const [childSaving, setChildSaving] = useState(false);
    const [addChildOpen, setAddChildOpen] = useState(false);
    const [newChildForm, setNewChildForm] = useState(EMPTY_NEW_CHILD);
    const [newChildSaving, setNewChildSaving] = useState(false);
    const [newChildPhotoUploading, setNewChildPhotoUploading] = useState(false);
    const [editAccount, setEditAccount] = useState(false);
    const [accountForm, setAccountForm] = useState({ name: "", email: "", contact_phone: "", username: "", current_password: "", new_password: "" });
    const [accountSaving, setAccountSaving] = useState(false);
    const [photoUploading, setPhotoUploading] = useState("");
    const [funds, setFunds] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        trackPage("/portal");
        // Kembali dari halaman pembayaran Midtrans: cek status terbaru dari server.
        const params = new URLSearchParams(window.location.search);
        const orderId = params.get("order_id");
        if (params.get("payment") === "finish" && orderId) {
            window.history.replaceState(null, "", window.location.pathname);
            const checkStatus = async (attempt = 0) => {
                try {
                    const { data: st } = await api.get(`/parent/payments/midtrans/${orderId}/status`);
                    if (st.status === "approved") {
                        toast.success("Pembayaran berhasil! Kwitansi terbit dan WhatsApp konfirmasi dikirim.");
                        setRefreshKey((k) => k + 1);
                    } else if (st.status === "failed") {
                        toast.error("Pembayaran tidak berhasil atau kedaluwarsa. Silakan coba lagi.");
                        setRefreshKey((k) => k + 1);
                    } else if (attempt < 5) {
                        setTimeout(() => checkStatus(attempt + 1), 3000);
                    } else {
                        toast.info("Pembayaran sedang diproses Midtrans. Status akan diperbarui otomatis.");
                    }
                } catch (e) {
                    if (attempt < 5) setTimeout(() => checkStatus(attempt + 1), 3000);
                }
            };
            checkStatus();
        }
        const loadParentData = () => {
            Promise.all([
                api.get("/parent/children"),
                api.get("/parent/report-cards"),
                api.get("/parent/fee-payments"),
                api.get("/parent/attendance"),
                api.get("/parent/achievements"),
                api.get("/parent/time-records"),
                api.get("/events/list"),
                api.get("/parent/billing"),
                api.get("/parent/payments"),
                api.get("/parent/funds"),
            ])
                .then(([childrenRes, reportsRes, paymentsRes, attendanceRes, achievementsRes, timeRecordsRes, eventsRes, billingRes, submissionsRes, fundsRes]) => {
                    setData(childrenRes.data);
                    setReports(reportsRes.data);
                    setPayments(paymentsRes.data);
                    setAttendance(attendanceRes.data);
                    setAchievements(achievementsRes.data);
                    setTimeRecords(timeRecordsRes.data);
                    setEventList(eventsRes.data);
                    setBilling(billingRes.data);
                    setSubmissions(submissionsRes.data);
                    setFunds(fundsRes.data);
                })
                .catch(() => setError("Gagal memuat data anak. Silakan coba lagi."));
        };
        loadParentData();
        return subscribeContentUpdates(loadParentData);
    }, [refreshKey]);

    const doLogout = async () => {
        await logout();
        navigate("/login");
    };

    const uploadAchievementImage = async (file) => {
        if (!file) return;
        setAchievementUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/parent/upload-achievement-image", fd);
            setAchievementForm((prev) => ({ ...prev, image_url: data.url }));
            toast.success("Foto prestasi terunggah");
        } catch {
            toast.error("Gagal mengunggah foto prestasi");
        } finally {
            setAchievementUploading(false);
        }
    };

    const refreshChildren = async () => {
        const { data: childrenData } = await api.get("/parent/children");
        setData(childrenData);
    };

    const openAddChild = () => {
        setNewChildForm(EMPTY_NEW_CHILD);
        setAddChildOpen(true);
    };

    const uploadNewChildPhoto = async (file) => {
        if (!file) return;
        setNewChildPhotoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: upload } = await api.post("/parent/upload-athlete-photo", fd);
            setNewChildForm((prev) => ({ ...prev, photo_url: upload.url }));
            toast.success("Foto anak terunggah");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setNewChildPhotoUploading(false);
        }
    };

    const saveNewChild = async (e) => {
        e.preventDefault();
        setNewChildSaving(true);
        try {
            await api.post("/parent/children", {
                ...newChildForm,
                joined_year: Number(newChildForm.joined_year),
            });
            await refreshChildren();
            toast.success("Data anak terkirim dan menunggu ACC admin");
            setAddChildOpen(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setNewChildSaving(false);
        }
    };

    const openEditChild = (child) => {
        setEditChild(child);
        setChildForm({
            name: child.name || "",
            nickname: child.nickname || "",
            gender: child.gender || "",
            birth_place: child.birth_place || "",
            birth_date: toInputDate(child.birth_date),
            religion: child.religion || "",
            address: child.address || "",
            father_name: child.father_name || "",
            mother_name: child.mother_name || "",
            student_phone: child.student_phone || "",
        });
    };

    const saveChild = async (e) => {
        e.preventDefault();
        setChildSaving(true);
        try {
            await api.put(`/parent/children/${editChild.id}`, childForm);
            await refreshChildren();
            toast.success("Data atlet diperbarui");
            setEditChild(null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setChildSaving(false);
        }
    };

    const openEditAccount = () => {
        const account = data?.parent || user || {};
        setAccountForm({
            name: account.name || "",
            email: account.email || "",
            contact_phone: account.contact_phone || "",
            username: account.username || "",
            current_password: "",
            new_password: "",
        });
        setEditAccount(true);
    };

    const saveAccount = async (e) => {
        e.preventDefault();
        setAccountSaving(true);
        try {
            const { data: updatedAccount } = await api.put("/parent/account", accountForm);
            setData((prev) => ({ ...prev, parent: updatedAccount }));
            updateUser(updatedAccount);
            toast.success("Data akun diperbarui");
            setEditAccount(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setAccountSaving(false);
        }
    };

    const uploadChildPhoto = async (child, file) => {
        if (!file) return;
        setPhotoUploading(child.id);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const upload = await api.post("/parent/upload-athlete-photo", fd);
            await api.put(`/parent/children/${child.id}/photo`, { photo_url: upload.data.url });
            setData((prev) => ({
                ...prev,
                children: prev.children.map((item) => (item.id === child.id ? { ...item, photo_url: upload.data.url } : item)),
            }));
            toast.success("Foto atlet diperbarui");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Gagal mengubah foto atlet");
        } finally {
            setPhotoUploading("");
        }
    };

    const submitAchievement = async (e) => {
        e.preventDefault();
        setAchievementSaving(true);
        try {
            const { data } = await api.post("/parent/achievements", {
                ...achievementForm,
                year: Number(achievementForm.year),
            });
            setAchievements((prev) => [data, ...prev]);
            setAchievementForm({ athlete_id: "", title: "", event: "", year: new Date().getFullYear(), medal: "gold", category: "Speed", level: "Kota/Kabupaten", image_url: "" });
            toast.success("Prestasi terkirim dan menunggu persetujuan admin");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Gagal mengirim prestasi");
        } finally {
            setAchievementSaving(false);
        }
    };

    if (error) {
        return (
            <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center px-6" data-testid="parent-dashboard-error">
                <p className="text-racing-red">{error}</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center" data-testid="parent-dashboard-loading">
                <span className="font-display uppercase text-black/30 text-xl tracking-widest animate-pulse">Memuat Portal</span>
            </div>
        );
    }

    const children = data.children || [];

    return (
        <div className="min-h-screen bg-[#FBF9F5] text-neutral-900" data-testid="parent-dashboard">
            <header className="border-b border-racing-line sticky top-0 bg-white/90 backdrop-blur-xl z-40">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <BrandLogo className="w-8 h-8" testid="parent-panel-logo" />
                        <div>
                            <p className="font-display uppercase tracking-wide text-sm leading-none">Portal Orang Tua</p>
                            <p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted mt-1">Mandalika Inlineskate</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={openEditAccount}
                            data-testid="parent-edit-account-button"
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors border border-racing-line px-4 py-2"
                        >
                            <PencilSimple size={16} /> Edit Akun
                        </button>
                        <button
                            onClick={doLogout}
                            data-testid="parent-logout-button"
                            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red transition-colors border border-racing-line px-4 py-2"
                        >
                            <SignOut size={16} /> Keluar
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-10 md:py-14">
                <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <p className="text-[11px] uppercase tracking-[0.35em] text-racing-gold mb-4">Data Privat Siswa</p>
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                        <div>
                            <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none">
                                Halo, <span className="text-racing-red">{data.parent?.name || user?.name}</span>
                            </h1>
                            <p className="mt-4 text-racing-muted max-w-2xl">
                                Berikut data siswa yang terhubung dengan akun Anda. Data ini hanya dapat dilihat setelah login.
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-px bg-racing-line border border-racing-line min-w-[280px]">
                            <div className="bg-white p-5" data-testid="parent-username-card">
                                <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-muted"><UserCircle size={16} /> Username</p>
                                <p className="mt-2 text-sm font-medium break-all">{data.parent?.username}</p>
                            </div>
                            <div className="bg-white p-5" data-testid="parent-children-count">
                                <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-muted"><Users size={16} /> Jumlah Anak</p>
                                <p className="mt-2 font-display text-4xl text-racing-red">{children.length}</p>
                            </div>
                        </div>
                    </div>
                </motion.section>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h2 className="font-display uppercase text-2xl">Data Anak Saya</h2>
                        <p className="text-sm text-racing-muted mt-1">Tambahkan anak lain ke akun yang sama. Data baru menunggu ACC admin.</p>
                    </div>
                    <button
                        type="button"
                        onClick={openAddChild}
                        data-testid="parent-add-child-button"
                        className="inline-flex items-center gap-2 bg-racing-red hover:bg-racing-redhover text-white text-xs uppercase tracking-[0.2em] px-5 py-3 transition-colors duration-300"
                    >
                        <Plus size={16} weight="bold" /> Tambah Anak
                    </button>
                </div>

                {children.length === 0 ? (
                    <div className="border border-racing-line bg-white p-10 text-center" data-testid="parent-children-empty">
                        <p className="text-racing-muted">Belum ada data anak yang terhubung ke akun ini. Gunakan tombol Tambah Anak di atas.</p>
                    </div>
                ) : (
                    <div className="space-y-8" data-testid="parent-children-list">
                        {children.map((child, index) => (
                            <motion.article
                                key={child.id}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, delay: index * 0.08 }}
                                className="bg-white border border-racing-line"
                                data-testid={`parent-child-card-${index}`}
                            >
                                <div className="border-b border-racing-line p-6 md:p-8 flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                                    <div className="flex items-start gap-5">
                                        <div className="shrink-0">
                                            {child.photo_url ? (
                                                <img src={fileUrl(child.photo_url)} alt={`Foto ${child.name}`} className="w-20 h-20 object-cover border border-racing-line" data-testid={`child-photo-${index}`} />
                                            ) : (
                                                <div className="w-20 h-20 bg-racing-surface border border-dashed border-racing-line flex items-center justify-center text-racing-muted"><Camera size={22} /></div>
                                            )}
                                            <label className="mt-2 inline-flex cursor-pointer items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-racing-red hover:text-racing-redhover" data-testid={`edit-child-photo-${index}`}>
                                                <Camera size={14} /> {photoUploading === child.id ? "Mengunggah..." : "Edit Foto"}
                                                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadChildPhoto(child, e.target.files?.[0])} />
                                            </label>
                                        </div>
                                        <div>
                                            <p className="text-[10px] uppercase tracking-[0.25em] text-racing-muted mb-2">{child.form_type === "siswa_baru" ? "Pendaftaran Siswa Baru" : child.source_sheet}{child.source_row ? ` • Baris ${child.source_row}` : ""}</p>
                                            <h2 className="font-display uppercase text-3xl md:text-4xl leading-none">{child.name}</h2>
                                            <p className="mt-3 text-sm text-racing-muted">
                                                {child.nickname ? `Panggilan: ${child.nickname} • ` : ""}{child.class_name || "—"} {child.package ? `• Paket ${child.package}` : ""}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
                                        <span className={`w-fit px-4 py-2 text-[10px] uppercase tracking-[0.2em] border ${child.status === "Aktif" ? "border-racing-gold text-racing-golddeep bg-racing-gold/10" : child.status === "Menunggu ACC" ? "border-racing-red text-racing-red bg-racing-red/5" : "border-racing-line text-racing-muted"}`} data-testid={`child-status-${index}`}>
                                            {child.status || "—"}
                                        </span>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => openEditChild(child)}
                                                data-testid={`edit-child-data-${index}`}
                                                className="inline-flex items-center gap-2 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 transition-colors duration-300"
                                            >
                                                <PencilSimple size={16} weight="bold" /> Edit Data
                                            </button>
                                            {child.status === "Aktif" && (
                                                <a
                                                    href={`/kartu/parent/${child.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`child-card-download-${index}`}
                                                    className="inline-flex items-center gap-2 bg-racing-red hover:bg-racing-redhover text-white text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 transition-colors duration-300"
                                                >
                                                    <IdentificationCard size={16} weight="bold" /> Kartu Atlet (PNG)
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6 md:p-8 space-y-8">
                                    <section>
                                        <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-racing-red mb-4"><IdentificationCard size={18} /> Data Siswa</h3>
                                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                            <Field label="Nomor Induk Atlet (NIA)" value={child.nia} />
                                            <Field label="Jenis Kelamin" value={child.gender} />
                                            <Field label="Tempat Lahir" value={child.birth_place} />
                                            <Field label="Tanggal Lahir" value={child.birth_date} />
                                            <Field label="Agama" value={child.religion} />
                                            <Field label="No. WhatsApp Siswa" value={child.student_phone} />
                                            <Field label="Email" value={child.email} />
                                            <Field label="Tanggal Bergabung" value={child.joined_date || child.joined_raw} />
                                            <Field label="Kode Promo" value={child.promo_code} />
                                        </div>
                                    </section>

                                    {(() => {
                                        const fund = (funds?.children || []).find((f) => f.athlete_id === child.public_athlete_id);
                                        if (!fund || fund.collected === 0) return null;
                                        return (
                                            <section data-testid={`parent-funds-${index}`}>
                                                <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-racing-red mb-4"><Money size={18} /> Uang Latihan dari Dukungan Publik</h3>
                                                <div className="border border-racing-line bg-racing-surface p-5">
                                                    <div className="flex flex-wrap gap-6">
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Terkumpul</p>
                                                            <p className="font-display text-2xl text-neutral-900">Rp {fund.collected.toLocaleString("id-ID")}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Sudah Diredeem</p>
                                                            <p className="font-display text-2xl text-neutral-900">Rp {fund.redeemed.toLocaleString("id-ID")}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Saldo Saat Ini</p>
                                                            <p className="font-display text-2xl text-racing-red" data-testid={`parent-fund-balance-${index}`}>Rp {fund.balance.toLocaleString("id-ID")}</p>
                                                        </div>
                                                    </div>
                                                    <div className="mt-4 space-y-1">
                                                        {(funds?.notes || []).map((note, i) => (
                                                            <p key={i} className="text-xs text-racing-muted" data-testid={`parent-fund-note-${i}`}>• {note}</p>
                                                        ))}
                                                        <p className="text-xs text-racing-muted">• Redeem dilakukan oleh admin/admin keuangan klub — hubungi admin untuk mencairkan.</p>
                                                    </div>
                                                </div>
                                            </section>
                                        );
                                    })()}

                                    <section>
                                        <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-racing-red mb-4"><WhatsappLogo size={18} /> Orang Tua / Wali</h3>
                                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                            <Field label="Nama Ayah/Wali" value={child.father_name} />
                                            <Field label="WhatsApp Ayah/Wali" value={child.father_phone} />
                                            <Field label="Nama Ibu/Wali" value={child.mother_name} />
                                            <Field label="WhatsApp Ibu/Wali" value={child.mother_phone} />
                                        </div>
                                    </section>

                                    <section>
                                        <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-racing-red mb-4"><MapPin size={18} /> Alamat & Sumber Pendaftaran</h3>
                                        <div className="grid md:grid-cols-2 gap-3">
                                            <Field label="Alamat Domisili" value={child.address} />
                                            <Field label="Alamat Sesuai KK" value={child.address_kk} />
                                            <Field label="Mengetahui Mandalis Dari" value={child.referral_source} />
                                            <Field label="Catatan Sumber Data" value={`${child.source_sheet} / baris ${child.source_row}`} />
                                        </div>
                                    </section>

                                    <section data-testid={`parent-dues-section-${index}`}>
                                        <DuesPaymentSection
                                            child={child}
                                            index={index}
                                            payments={payments}
                                            billing={billing}
                                            submissions={submissions}
                                            onPay={(billingChild, type) => setPaymentTarget({ child: billingChild, type })}
                                        />
                                    </section>

                                    <section data-testid={`parent-attendance-section-${index}`}>
                                        <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-racing-red mb-4"><CalendarCheck size={18} /> Riwayat Absensi</h3>
                                        {attendance.filter((record) => record.athlete_id === child.id).length === 0 ? (
                                            <div className="bg-racing-surface border border-racing-line p-4 text-sm text-racing-muted">Belum ada absensi yang tercatat.</div>
                                        ) : (
                                            <div className="grid md:grid-cols-2 gap-3">
                                                {attendance.filter((record) => record.athlete_id === child.id).map((record, attendanceIndex) => (
                                                    <div key={record.id} className="bg-racing-surface border border-racing-line p-4" data-testid={`parent-attendance-${index}-${attendanceIndex}`}>
                                                        <p className="text-[10px] uppercase tracking-[0.2em] text-racing-golddeep">{record.date}</p>
                                                        <p className="mt-2 font-display uppercase text-2xl">{record.status}</p>
                                                        <p className="mt-2 text-xs text-racing-muted">{record.class_name} • Coach {record.coach_name || "—"}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </section>

                                    <section data-testid={`parent-time-records-section-${index}`}>
                                        <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-racing-red mb-4"><Timer size={18} /> Catatan Waktu</h3>
                                        {timeRecords.filter((record) => record.athlete_id === child.id).length === 0 ? (
                                            <div className="bg-racing-surface border border-racing-line p-4 text-sm text-racing-muted">Belum ada catatan waktu yang diinput pelatih.</div>
                                        ) : (
                                            <div className="space-y-5">
                                                {groupTimeRecordsByRace(timeRecords.filter((record) => record.athlete_id === child.id)).map((group, groupIndex) => (
                                                    <div key={group.name} className="border border-racing-line bg-racing-surface" data-testid={`parent-time-group-${index}-${groupIndex}`}>
                                                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3 border-b border-racing-line bg-white">
                                                            <span className="font-display uppercase text-lg text-neutral-900">{group.name}</span>
                                                            <span className="text-xs text-racing-muted">{group.items.length} catatan</span>
                                                            {group.best && (
                                                                <span className="ml-auto px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] border border-racing-gold text-racing-golddeep bg-racing-gold/10" data-testid={`parent-time-group-best-${index}-${groupIndex}`}>
                                                                    Terbaik {group.best.time_text}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="px-2 pt-3 bg-white border-b border-racing-line" data-testid={`parent-time-chart-${index}-${groupIndex}`}>
                                                            <TimeProgressChart records={group.items} />
                                                            <p className="px-2 pb-2 text-[10px] text-racing-muted">Garis naik = waktu anak semakin kecil, berarti semakin kencang.</p>
                                                        </div>
                                                        <div className="grid md:grid-cols-2 gap-3 p-3">
                                                            {group.items.map((record, recordIndex) => (
                                                                <div key={record.id} className="bg-white border border-racing-line p-4" data-testid={`parent-time-record-${index}-${groupIndex}-${recordIndex}`}>
                                                                    <p className="text-[10px] uppercase tracking-[0.2em] text-racing-golddeep">{record.record_date} • {record.event_number}</p>
                                                                    <p className="mt-2 font-display text-3xl text-racing-red">
                                                                        {record.time_text}
                                                                        {record.is_best && <span className="ml-3 align-middle px-2 py-1 text-[9px] uppercase tracking-[0.15em] border border-racing-gold text-racing-golddeep bg-racing-gold/10">Terbaik</span>}
                                                                    </p>
                                                                    <p className="mt-2 text-xs text-racing-muted">{record.note ? `${record.note} • ` : ""}Dicatat oleh {record.created_by_name || "Pelatih"}</p>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </section>

                                    <section data-testid={`parent-report-section-${index}`}>
                                        <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-racing-red mb-4"><ClipboardText size={18} /> Rapot</h3>
                                        {visibleReports.filter((report) => report.athlete_id === child.id).length === 0 ? (
                                            <div className="bg-racing-surface border border-racing-line p-4 text-sm text-racing-muted">Belum ada rapot yang diisi pelatih.</div>
                                        ) : (
                                            <div className="space-y-4">
                                                {buildReportProgress(visibleReports, child.id).length > 0 && (
                                                    <div className="bg-white border border-racing-line p-4" data-testid={`parent-report-progress-${index}`}>
                                                        <p className="text-[10px] uppercase tracking-[0.2em] text-racing-golddeep mb-3">Progres {child.nickname || child.name.split(" ")[0]} per Bulan</p>
                                                        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                                                            {buildReportProgress(visibleReports, child.id).slice(0, 6).map((series) => (
                                                                <ReportProgressChart key={series.key} series={series} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {visibleReports.filter((report) => report.athlete_id === child.id).map((report, reportIndex) => (
                                                    <div key={report.id} className="bg-racing-surface border border-racing-line p-5" data-testid={`parent-report-${index}-${reportIndex}`}>
                                                        <p className="text-[10px] uppercase tracking-[0.2em] text-racing-golddeep">{formatReportPeriod(report.period)}</p>
                                                        <h4 className="font-display uppercase text-2xl mt-1">{report.template?.title || "Rapot"}</h4>
                                                        <div className="mt-4 grid md:grid-cols-2 gap-3">
                                                            {(report.template?.fields || []).map((field) => (
                                                                <div key={field.id} className="bg-white border border-racing-line p-3">
                                                                    <p className="text-[10px] uppercase tracking-[0.15em] text-racing-muted mb-1">{field.label}</p>
                                                                    {field.type === "percent" && report.answers?.[field.id] != null ? (
                                                                        <div data-testid={`parent-report-percent-${index}-${reportIndex}`}>
                                                                            <p className="font-display text-2xl text-racing-red">{report.answers[field.id]}%</p>
                                                                            <div className="mt-1.5 h-3 bg-racing-surface border border-racing-line" data-testid={`parent-report-percent-bar-${index}-${reportIndex}`}>
                                                                                <div className="h-full bg-racing-red" style={{ width: `${Math.min(100, Math.max(0, Number(report.answers[field.id]) || 0))}%` }} />
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-sm text-neutral-900 whitespace-pre-line">{formatReportValue(report.answers?.[field.id])}</p>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <p className="mt-4 text-sm italic text-neutral-800 border-t border-racing-line pt-3" data-testid={`parent-report-closing-${index}-${reportIndex}`}>&ldquo;{REPORT_CLOSING_MESSAGE}&rdquo;</p>
                                                        <p className="mt-2 text-[11px] uppercase tracking-[0.15em] text-racing-muted" data-testid={`parent-report-coach-${index}-${reportIndex}`}>Diisi oleh Pelatih {report.coach_name || "—"}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </section>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}

                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55 }}
                    className="mt-12 bg-white border border-racing-line p-6 md:p-8"
                    data-testid="parent-achievement-section"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <span className="w-10 h-10 bg-racing-red text-white flex items-center justify-center"><Trophy size={20} /></span>
                        <div>
                            <h2 className="font-display uppercase text-3xl">Tambah Prestasi</h2>
                            <p className="text-sm text-racing-muted mt-1">Prestasi yang Anda kirim akan dicek admin dulu sebelum tampil di halaman publik.</p>
                        </div>
                    </div>

                    {children.length === 0 ? (
                        <p className="border border-racing-line bg-racing-surface p-4 text-sm text-racing-muted" data-testid="achievement-no-active-athlete">
                            Tambahkan data atlet yang aktif terlebih dahulu sebelum mengirim prestasi.
                        </p>
                    ) : (
                    <form onSubmit={submitAchievement} className="grid md:grid-cols-3 gap-4" data-testid="parent-achievement-form">
                        <select required value={achievementForm.athlete_id} onChange={(e) => setAchievementForm({ ...achievementForm, athlete_id: e.target.value })} data-testid="achievement-athlete-select" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm">
                            <option value="" disabled>Pilih atlet</option>
                            {children.map((child) => <option key={child.id} value={child.id}>{child.name}</option>)}
                        </select>
                        <input required value={achievementForm.title} onChange={(e) => setAchievementForm({ ...achievementForm, title: e.target.value })} data-testid="achievement-title-input" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Nomor lomba (mis. Speed 500m Putra)" />
                        <input
                            required
                            list="parent-event-list"
                            value={achievementForm.event}
                            onChange={(e) => setAchievementForm({ ...achievementForm, event: e.target.value })}
                            data-testid="achievement-event-input"
                            className="bg-racing-surface border border-racing-line px-3 py-3 text-sm"
                            placeholder="Ketik atau pilih nama event"
                        />
                        <datalist id="parent-event-list" data-testid="achievement-event-list">
                            {eventList.map((eventName) => <option key={eventName} value={eventName} />)}
                        </datalist>
                        <input required type="number" min="2000" max={new Date().getFullYear() + 1} value={achievementForm.year} onChange={(e) => setAchievementForm({ ...achievementForm, year: e.target.value })} data-testid="achievement-year-input" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Tahun" />
                        <select value={achievementForm.medal} onChange={(e) => setAchievementForm({ ...achievementForm, medal: e.target.value })} data-testid="achievement-medal-select" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm">
                            <option value="gold">Emas</option><option value="silver">Perak</option><option value="bronze">Perunggu</option>
                        </select>
                        <select value={achievementForm.category} onChange={(e) => setAchievementForm({ ...achievementForm, category: e.target.value })} data-testid="achievement-category-select" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm">
                            <option>Speed</option><option>Standart</option><option>Slalom</option>
                        </select>
                        <select value={achievementForm.level} onChange={(e) => setAchievementForm({ ...achievementForm, level: e.target.value })} data-testid="achievement-level-select" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm">
                            <option>Internal Klub</option><option>Kota/Kabupaten</option><option>Provinsi</option><option>Nasional</option><option>Internasional</option>
                        </select>
                        <div className="md:col-span-2">
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Foto Bukti Prestasi (Opsional)</label>
                            {achievementForm.image_url && <img src={fileUrl(achievementForm.image_url)} alt="Bukti prestasi" className="mb-3 h-28 w-full object-cover border border-racing-line" />}
                            <input type="file" accept="image/*" onChange={(e) => uploadAchievementImage(e.target.files?.[0])} data-testid="achievement-image-input" className="w-full text-xs text-racing-muted" />
                            {achievementUploading && <p className="text-xs text-racing-red mt-1">Mengunggah foto...</p>}
                        </div>
                        <button disabled={achievementSaving || achievementUploading || children.length === 0} data-testid="achievement-submit-button" className="bg-racing-red text-white px-5 py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">
                            {achievementSaving ? "Mengirim..." : "Kirim Prestasi"}
                        </button>
                    </form>
                    )}

                    <div className="mt-8 border-t border-racing-line pt-6">
                        <h3 className="font-display uppercase text-xl mb-4">Riwayat Prestasi yang Dikirim</h3>
                        {achievements.length === 0 ? (
                            <p className="text-sm text-racing-muted" data-testid="parent-achievement-empty">Belum ada prestasi yang dikirim.</p>
                        ) : (
                            <div className="grid md:grid-cols-2 gap-3">
                                {achievements.map((achievement, index) => (
                                    <div key={achievement.id} className="bg-racing-surface border border-racing-line p-4" data-testid={`parent-achievement-${index}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="font-display uppercase text-xl">{achievement.title}</p>
                                                <p className="text-xs text-racing-muted mt-1">{achievement.athlete} • {achievement.event} • {achievement.year}</p>
                                            </div>
                                            <span className={`px-3 py-1 text-[10px] uppercase tracking-[0.15em] border ${achievement.status === "approved" ? "border-green-700 text-green-700" : achievement.status === "rejected" ? "border-racing-red text-racing-red" : "border-racing-gold text-racing-golddeep"}`}>
                                                {achievement.status === "approved" ? "Tampil" : achievement.status === "rejected" ? "Ditolak" : "Menunggu ACC"}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </motion.section>
            </main>

            <Dialog open={addChildOpen} onOpenChange={setAddChildOpen}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="parent-add-child-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase text-2xl">Tambah Anak</DialogTitle>
                        <DialogDescription className="text-sm text-racing-muted">Data anak baru akan masuk ke akun ini dan berstatus Menunggu ACC sampai disetujui admin.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveNewChild} className="grid sm:grid-cols-2 gap-4 mt-5" data-testid="parent-add-child-form">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Lengkap</label>
                            <input required value={newChildForm.full_name} onChange={(e) => setNewChildForm({ ...newChildForm, full_name: e.target.value })} data-testid="new-child-full-name-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Panggilan</label>
                            <input value={newChildForm.nickname} onChange={(e) => setNewChildForm({ ...newChildForm, nickname: e.target.value })} data-testid="new-child-nickname-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tempat Lahir</label>
                            <input required value={newChildForm.birth_place} onChange={(e) => setNewChildForm({ ...newChildForm, birth_place: e.target.value })} data-testid="new-child-birth-place-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal Lahir</label>
                            <input required type="date" value={newChildForm.birth_date} onChange={(e) => setNewChildForm({ ...newChildForm, birth_date: e.target.value })} data-testid="new-child-birth-date-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Jenis Kelamin</label>
                            <select required value={newChildForm.gender} onChange={(e) => setNewChildForm({ ...newChildForm, gender: e.target.value })} data-testid="new-child-gender-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                                <option value="" disabled>Pilih</option>
                                <option value="Laki-laki">Laki-laki</option>
                                <option value="Perempuan">Perempuan</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label>
                            <select required value={newChildForm.class_name} onChange={(e) => setNewChildForm({ ...newChildForm, class_name: e.target.value })} data-testid="new-child-class-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                                <option value="" disabled>Pilih kelas</option>
                                {CLASS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Masuk Mandalis Sejak</label>
                            <input required type="number" min="2000" max={new Date().getFullYear() + 1} value={newChildForm.joined_year} onChange={(e) => setNewChildForm({ ...newChildForm, joined_year: e.target.value })} data-testid="new-child-joined-year-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Agama</label>
                            <input value={newChildForm.religion} onChange={(e) => setNewChildForm({ ...newChildForm, religion: e.target.value })} data-testid="new-child-religion-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Alamat Domisili</label>
                            <textarea rows={3} value={newChildForm.address} onChange={(e) => setNewChildForm({ ...newChildForm, address: e.target.value })} data-testid="new-child-address-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Mengetahui Mandalis Dari</label>
                            <input value={newChildForm.referral_source} onChange={(e) => setNewChildForm({ ...newChildForm, referral_source: e.target.value })} data-testid="new-child-referral-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kode Promo</label>
                            <input value={newChildForm.promo_code} onChange={(e) => setNewChildForm({ ...newChildForm, promo_code: e.target.value })} data-testid="new-child-promo-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Foto Anak (Opsional)</label>
                            <div className="flex items-center gap-4">
                                {newChildForm.photo_url ? (
                                    <img src={fileUrl(newChildForm.photo_url)} alt="Foto anak" className="w-20 h-24 object-cover border border-racing-line" data-testid="new-child-photo-preview" />
                                ) : (
                                    <div className="w-20 h-24 border border-dashed border-racing-line bg-racing-surface flex items-center justify-center text-racing-muted"><Camera size={22} /></div>
                                )}
                                <label className="inline-flex cursor-pointer items-center gap-2 border border-racing-line px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red hover:border-racing-red" data-testid="new-child-photo-upload-label">
                                    <Camera size={15} /> {newChildPhotoUploading ? "Mengunggah..." : "Unggah Foto"}
                                    <input type="file" accept="image/*" className="hidden" data-testid="new-child-photo-input" onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; uploadNewChildPhoto(file); }} />
                                </label>
                            </div>
                        </div>
                        <button disabled={newChildSaving || newChildPhotoUploading} data-testid="save-new-child-button" className="sm:col-span-2 bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">
                            {newChildSaving ? "Mengirim..." : "Kirim untuk ACC Admin"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={!!editChild} onOpenChange={(open) => { if (!open) setEditChild(null); }}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="parent-edit-child-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase text-2xl">Edit Data Atlet</DialogTitle>
                        <DialogDescription className="sr-only">Formulir untuk memperbarui data atlet.</DialogDescription>
                    </DialogHeader>
                    {childForm && (
                        <form onSubmit={saveChild} className="grid sm:grid-cols-2 gap-4 mt-2" data-testid="parent-edit-child-form">
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Lengkap</label>
                                <input required value={childForm.name} onChange={(e) => setChildForm({ ...childForm, name: e.target.value })} data-testid="edit-child-name-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Panggilan</label>
                                <input value={childForm.nickname} onChange={(e) => setChildForm({ ...childForm, nickname: e.target.value })} data-testid="edit-child-nickname-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Jenis Kelamin</label>
                                <select value={childForm.gender} onChange={(e) => setChildForm({ ...childForm, gender: e.target.value })} data-testid="edit-child-gender-select" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                                    <option value="">Pilih</option>
                                    <option value="Laki-laki">Laki-laki</option>
                                    <option value="Perempuan">Perempuan</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tempat Lahir</label>
                                <input value={childForm.birth_place} onChange={(e) => setChildForm({ ...childForm, birth_place: e.target.value })} data-testid="edit-child-birth-place-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal Lahir</label>
                                <input type="date" value={childForm.birth_date} onChange={(e) => setChildForm({ ...childForm, birth_date: e.target.value })} data-testid="edit-child-birth-date-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Agama</label>
                                <input value={childForm.religion} onChange={(e) => setChildForm({ ...childForm, religion: e.target.value })} data-testid="edit-child-religion-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Ayah/Wali</label>
                                <input value={childForm.father_name} onChange={(e) => setChildForm({ ...childForm, father_name: e.target.value })} data-testid="edit-child-father-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Ibu/Wali</label>
                                <input value={childForm.mother_name} onChange={(e) => setChildForm({ ...childForm, mother_name: e.target.value })} data-testid="edit-child-mother-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">No. WhatsApp Siswa</label>
                                <input value={childForm.student_phone} onChange={(e) => setChildForm({ ...childForm, student_phone: e.target.value })} data-testid="edit-child-phone-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Alamat Domisili</label>
                                <textarea rows={3} value={childForm.address} onChange={(e) => setChildForm({ ...childForm, address: e.target.value })} data-testid="edit-child-address-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                            <button disabled={childSaving} data-testid="save-child-data-button" className="sm:col-span-2 bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">
                                {childSaving ? "Menyimpan..." : "Simpan Data Atlet"}
                            </button>
                        </form>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={editAccount} onOpenChange={setEditAccount}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-lg" data-testid="parent-edit-account-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase text-2xl">Edit Akun</DialogTitle>
                        <DialogDescription className="sr-only">Formulir untuk memperbarui data akun orang tua.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveAccount} className="space-y-4 mt-2" data-testid="parent-edit-account-form">
                        <div className="bg-racing-surface border border-racing-line p-3 text-sm">
                            <span className="text-racing-muted">Username:</span> <span className="font-medium" data-testid="account-username-readonly">{data.parent?.username || user?.username}</span>
                        </div>
                        {[
                            ["name", "Nama Akun", "text"],
                            ["username", "Username", "text"],
                            ["email", "Email", "email"],
                            ["contact_phone", "No. WhatsApp", "text"],
                        ].map(([name, label, type]) => (
                            <div key={name}>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">{label}</label>
                                <input required type={type} value={accountForm[name]} onChange={(e) => setAccountForm({ ...accountForm, [name]: e.target.value })} data-testid={`account-${name}-input`} className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                                {name === "username" && (
                                    <p className="mt-1.5 text-xs text-racing-red" data-testid="account-username-hint">Tanpa spasi, minimal 3 karakter. Contoh: budi.santoso</p>
                                )}
                            </div>
                        ))}
                        <div className="border-t border-racing-line pt-4">
                            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-3"><Key size={15} /> Ganti Password (Opsional)</p>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <input type="password" value={accountForm.current_password} onChange={(e) => setAccountForm({ ...accountForm, current_password: e.target.value })} required={!!accountForm.new_password} data-testid="account-current-password-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" placeholder="Password saat ini" />
                                <input type="password" minLength={8} value={accountForm.new_password} onChange={(e) => setAccountForm({ ...accountForm, new_password: e.target.value })} data-testid="account-new-password-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" placeholder="Password baru" />
                            </div>
                            <p className="mt-2 text-xs text-racing-muted">Password baru minimal 8 karakter. Password lama tidak berlaku lagi setelah berhasil diganti. Jika akun dibuat lewat Google dan belum pernah punya password, kolom password saat ini boleh dikosongkan.</p>
                        </div>
                        <button disabled={accountSaving} data-testid="save-account-data-button" className="w-full bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-50">
                            {accountSaving ? "Menyimpan..." : "Simpan Akun"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            <PaymentDialog
                open={!!paymentTarget}
                onClose={() => setPaymentTarget(null)}
                child={paymentTarget?.child}
                paymentInfo={billing}
                type={paymentTarget?.type || "dues"}
                onSubmitted={() => setRefreshKey((k) => k + 1)}
            />
        </div>
    );
}
